import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';
import '../styles/Home.css';
import { AuthContext } from './../AuthContext';
import api from './../api';

function formatMoney(value, compact = false) {
  const amount = Number(value || 0);
  return amount.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: compact ? 'compact' : 'standard',
    minimumFractionDigits: compact ? 0 : 2,
    maximumFractionDigits: compact ? 1 : 2,
  });
}

function formatDate(value) {
  if (!value) {
    return '-';
  }
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function getRiskCopy(riskLevel, forecast) {
  const finalCash = Number(forecast?.next_30_days_cash_after_payments || 0);
  if (riskLevel === 'danger') {
    return {
      title: `Short by ${formatMoney(Math.abs(Math.min(finalCash, 0)))}`,
      detail: 'Cash flow goes negative inside the forecast window.',
    };
  }
  if (riskLevel === 'warning') {
    return {
      title: 'Watch the pressure',
      detail: 'The forecast is positive, but one or more accounts need attention.',
    };
  }
  return {
    title: 'Covered for now',
    detail: 'Expected income and cash cover the visible obligations.',
  };
}

function buildCashFlowGeometry(days) {
  const width = 920;
  const height = 360;
  const padding = { top: 26, right: 30, bottom: 46, left: 74 };
  const plotWidth = width - padding.left - padding.right;
  const plotHeight = height - padding.top - padding.bottom;
  const maxFlow = Math.max(
    1,
    ...days.flatMap((day) => [
      Number(day.income || 0),
      Number(day.payments || 0) + Number(day.expenses || 0),
      Math.abs(Number(day.projected_cash || 0)),
    ])
  );
  const minCash = Math.min(0, ...days.map((day) => Number(day.projected_cash || 0)));
  const maxCash = Math.max(1, ...days.map((day) => Number(day.projected_cash || 0)));
  const cashRange = Math.max(1, maxCash - minCash);
  const step = days.length > 1 ? plotWidth / (days.length - 1) : plotWidth;
  const zeroY = padding.top + plotHeight * 0.46;
  const maxBarHeight = plotHeight * 0.38;

  const cashY = (value) => {
    const normalized = (Number(value || 0) - minCash) / cashRange;
    return padding.top + plotHeight - normalized * plotHeight;
  };

  return {
    width,
    height,
    padding,
    plotWidth,
    plotHeight,
    maxFlow,
    step,
    zeroY,
    maxBarHeight,
    cashY,
    xForIndex: (index) => padding.left + (days.length === 1 ? plotWidth / 2 : index * step),
  };
}

function CashFlowChart({ daily }) {
  const days = (daily || []).slice(0, 30);
  const geometry = useMemo(() => buildCashFlowGeometry(days.length ? days : [{}]), [days]);

  if (!days.length) {
    return <div className="empty-state">No daily forecast data yet.</div>;
  }

  const barWidth = clamp(geometry.plotWidth / Math.max(days.length, 1) * 0.38, 7, 18);
  const cashPoints = days
    .map((day, index) => `${geometry.xForIndex(index)},${geometry.cashY(day.projected_cash)}`)
    .join(' ');
  const cashArea = `${geometry.padding.left},${geometry.cashY(0)} ${cashPoints} ${geometry.xForIndex(days.length - 1)},${geometry.cashY(0)}`;

  return (
    <div className="cashflow-shell">
      <svg className="cashflow-chart" viewBox={`0 0 ${geometry.width} ${geometry.height}`} role="img" aria-label="30 day cash flow forecast">
        <defs>
          <linearGradient id="cashAreaFill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#1f8a70" stopOpacity="0.28" />
            <stop offset="100%" stopColor="#1f8a70" stopOpacity="0.04" />
          </linearGradient>
        </defs>
        <line x1={geometry.padding.left} x2={geometry.width - geometry.padding.right} y1={geometry.zeroY} y2={geometry.zeroY} className="axis-zero" />
        <line x1={geometry.padding.left} x2={geometry.width - geometry.padding.right} y1={geometry.cashY(0)} y2={geometry.cashY(0)} className="axis-cash-zero" />
        <text x={geometry.padding.left - 12} y={geometry.zeroY + 4} className="axis-label" textAnchor="end">flow</text>
        <text x={geometry.padding.left - 12} y={geometry.cashY(0) + 4} className="axis-label" textAnchor="end">cash</text>

        <polygon points={cashArea} className="cash-area" />

        {days.map((day, index) => {
          const x = geometry.xForIndex(index);
          const incomeHeight = (Number(day.income || 0) / geometry.maxFlow) * geometry.maxBarHeight;
          const outflow = Number(day.payments || 0) + Number(day.expenses || 0);
          const outflowHeight = (outflow / geometry.maxFlow) * geometry.maxBarHeight;
          const isNegative = Number(day.projected_cash || 0) < 0;
          return (
            <g key={`flow-${day.date || index}`}>
              <line x1={x} x2={x} y1={geometry.padding.top} y2={geometry.height - geometry.padding.bottom} className="day-grid" />
              {incomeHeight > 0 && (
                <rect
                  x={x - barWidth / 2}
                  y={geometry.zeroY - incomeHeight}
                  width={barWidth}
                  height={incomeHeight}
                  rx="3"
                  className="income-bar"
                />
              )}
              {outflowHeight > 0 && (
                <rect
                  x={x - barWidth / 2}
                  y={geometry.zeroY}
                  width={barWidth}
                  height={outflowHeight}
                  rx="3"
                  className="expense-bar"
                />
              )}
              <circle cx={x} cy={geometry.cashY(day.projected_cash)} r={isNegative ? 5 : 3.5} className={isNegative ? 'cash-dot danger-dot' : 'cash-dot'} />
              {(index === 0 || index === days.length - 1 || index % 5 === 0) && (
                <text x={x} y={geometry.height - 18} className="date-label" textAnchor="middle">
                  {formatDate(day.date)}
                </text>
              )}
              <title>
                {`${formatDate(day.date)}\nIncome: ${formatMoney(day.income)}\nOut: ${formatMoney(outflow)}\nProjected cash: ${formatMoney(day.projected_cash)}`}
              </title>
            </g>
          );
        })}

        <polyline points={cashPoints} className="cash-line" />
      </svg>
      <div className="chart-legend">
        <span><i className="legend-income" /> income</span>
        <span><i className="legend-expense" /> payments + expenses</span>
        <span><i className="legend-cash" /> projected cash</span>
      </div>
    </div>
  );
}

function WaterfallChart({ cash, income, obligations, forecast }) {
  const steps = [
    { label: 'Cash now', value: Number(cash?.total || 0), kind: 'base' },
    { label: 'Income', value: Number(income?.next_30_days_expected || 0), kind: 'income' },
    { label: 'Payments', value: -Number(obligations?.next_30_days_unpaid || 0), kind: 'expense' },
    { label: 'Expenses', value: -Number(obligations?.next_30_days_standalone_expenses || 0), kind: 'expense' },
    { label: 'End cash', value: Number(forecast?.next_30_days_cash_after_payments || 0), kind: 'total' },
  ];
  const maxMagnitude = Math.max(1, ...steps.map((step) => Math.abs(step.value)));

  return (
    <div className="waterfall">
      {steps.map((step) => {
        const height = clamp((Math.abs(step.value) / maxMagnitude) * 120, step.value === 0 ? 4 : 16, 120);
        return (
          <div className="waterfall-step" key={step.label}>
            <div className="waterfall-rail">
              <div className={`waterfall-bar ${step.kind}`} style={{ height: `${height}px` }} />
            </div>
            <strong>{formatMoney(step.value, true)}</strong>
            <span>{step.label}</span>
          </div>
        );
      })}
    </div>
  );
}

function AccountLanes({ accounts }) {
  const rows = (accounts || []).slice(0, 6);
  const maxBalance = Math.max(1, ...rows.map((account) => Math.abs(Number(account.current_balance || 0))));

  if (!rows.length) {
    return <div className="empty-state">No active cash accounts.</div>;
  }

  return (
    <div className="account-lanes">
      {rows.map((account) => {
        const balance = Number(account.current_balance || 0);
        const due = Number(account.assigned_unpaid_current_period || 0);
        const width = clamp((Math.abs(balance) / maxBalance) * 100, 3, 100);
        const dueWidth = clamp((due / Math.max(Math.abs(balance), due, 1)) * 100, due > 0 ? 3 : 0, 100);
        const isShort = Number(account.shortage_current_period || 0) > 0;
        return (
          <div className={`account-lane ${isShort ? 'is-short' : ''}`} key={account.id}>
            <div className="lane-label">
              <strong>{account.name}</strong>
              <span>{formatMoney(balance)}</span>
            </div>
            <div className="lane-track">
              <div className="lane-balance" style={{ width: `${width}%` }} />
              {due > 0 && <div className="lane-due" style={{ width: `${dueWidth}%` }} />}
            </div>
            <div className="lane-meta">
              <span>due this period {formatMoney(due)}</span>
              <span>after {formatMoney(account.left_after_current_period)}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function AgentCheckIn({ forecast, onDashboardRefresh }) {
  const [mode, setMode] = useState('questions');
  const [balanceText, setBalanceText] = useState('');
  const [parseResult, setParseResult] = useState(null);
  const [selectedDrafts, setSelectedDrafts] = useState({});
  const [parseLoading, setParseLoading] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);
  const [agentMessage, setAgentMessage] = useState('');
  const questions = forecast?.questions || [];
  const drafts = parseResult?.drafts || [];
  const selectedReadyDrafts = drafts.filter((draft) => selectedDrafts[draft.line_no] && draft.status === 'ready');

  const parseBalanceText = async () => {
    if (!balanceText.trim()) {
      setAgentMessage('Paste balances first.');
      return;
    }
    try {
      setParseLoading(true);
      setAgentMessage('');
      const response = await api.post('/planning/parse-balances-text', { text: balanceText });
      const result = response.data || {};
      const readySelections = {};
      (result.drafts || []).forEach((draft) => {
        if (draft.status === 'ready') {
          readySelections[draft.line_no] = true;
        }
      });
      setParseResult(result);
      setSelectedDrafts(readySelections);
      setAgentMessage(`${result.ready_count || 0} ready, ${result.needs_review_count || 0} need review.`);
    } catch (err) {
      setAgentMessage('Could not parse this text yet.');
    } finally {
      setParseLoading(false);
    }
  };

  const applySelectedDrafts = async () => {
    if (!selectedReadyDrafts.length) {
      setAgentMessage('Select at least one ready draft.');
      return;
    }
    try {
      setApplyLoading(true);
      setAgentMessage('');
      const items = selectedReadyDrafts.map((draft) => ({
        source_type: draft.source_type,
        source_id: draft.source_id,
        field: draft.field,
        amount: draft.amount,
        raw_input: draft.raw,
      }));
      const response = await api.post('/planning/confirm-balances', { items });
      const updatedCount = (response.data?.updated || []).length;
      const rejectedCount = (response.data?.rejected || []).length;
      setAgentMessage(`Updated ${updatedCount}. Rejected ${rejectedCount}.`);
      await onDashboardRefresh?.();
    } catch (err) {
      setAgentMessage('Could not apply the selected drafts.');
    } finally {
      setApplyLoading(false);
    }
  };

  return (
    <section className="panel agent-panel">
      <div className="panel-heading">
        <div>
          <span className="eyebrow">Agent check-in</span>
          <h3>Current situation</h3>
        </div>
        <div className="segmented-control" aria-label="Balance update mode">
          <button className={mode === 'questions' ? 'active' : ''} onClick={() => setMode('questions')} type="button">Ask</button>
          <button className={mode === 'paste' ? 'active' : ''} onClick={() => setMode('paste')} type="button">Paste</button>
          <button className={mode === 'manual' ? 'active' : ''} onClick={() => setMode('manual')} type="button">Manual</button>
        </div>
      </div>

      {mode === 'questions' && (
        <div className="agent-questions">
          {(questions.length ? questions : ['Do you want to update balances before I trust this forecast?']).slice(0, 4).map((question, index) => (
            <div className="agent-question" key={`agent-question-${index}`}>
              <span>{question}</span>
              <button type="button">Answer</button>
            </div>
          ))}
        </div>
      )}

      {mode === 'paste' && (
        <div className="balance-input-draft">
          <textarea
            value={balanceText}
            onChange={(event) => setBalanceText(event.target.value)}
            placeholder={'Chase Checking - 1250.40\nApple Card available 1200\nRent paid'}
            aria-label="Paste balances or payment updates"
          />
          <div className="draft-actions">
            <span>{agentMessage || 'Paste account/card balances, then confirm before applying.'}</span>
            <button type="button" onClick={parseBalanceText} disabled={parseLoading}>
              {parseLoading ? 'Parsing...' : 'Parse draft'}
            </button>
          </div>
          {!!drafts.length && (
            <div className="draft-table" aria-label="Parsed balance drafts">
              {drafts.map((draft) => (
                <label className={`draft-row ${draft.status}`} key={`draft-${draft.line_no}`}>
                  <input
                    type="checkbox"
                    checked={!!selectedDrafts[draft.line_no]}
                    disabled={draft.status !== 'ready'}
                    onChange={(event) => setSelectedDrafts((prev) => ({ ...prev, [draft.line_no]: event.target.checked }))}
                  />
                  <span>
                    <strong>{draft.matched_name || draft.source_label}</strong>
                    <small>{draft.field || draft.kind} · confidence {Math.round(Number(draft.confidence || 0) * 100)}%</small>
                  </span>
                  <b>{formatMoney(draft.amount)}</b>
                </label>
              ))}
              <div className="draft-actions">
                <span>Only checked ready drafts will update records.</span>
                <button type="button" onClick={applySelectedDrafts} disabled={applyLoading || !selectedReadyDrafts.length}>
                  {applyLoading ? 'Applying...' : 'Apply selected'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {mode === 'manual' && (
        <div className="manual-grid">
          <input placeholder="Source" aria-label="Source" />
          <input placeholder="Amount" aria-label="Amount" />
          <select aria-label="Kind" defaultValue="cash">
            <option value="cash">cash balance</option>
            <option value="card">card available</option>
            <option value="paid">payment paid</option>
          </select>
          <button type="button" disabled>Add draft</button>
        </div>
      )}
    </section>
  );
}

function Home({ onLogout }) {
  const { isAuthenticated, token } = useContext(AuthContext);
  const [menuOpen, setMenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboard, setDashboard] = useState(null);

  const toggleMenu = () => setMenuOpen((prev) => !prev);

  const fetchDashboard = useCallback(async ({ silent = false } = {}) => {
    if (!isAuthenticated || !token) {
      return;
    }

    try {
      if (!silent) {
        setLoading(true);
      }
      setError(null);
      const response = await api.get('/planning/dashboard');
      setDashboard(response.data || null);
    } catch (err) {
      setError('Failed to load planning dashboard');
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [isAuthenticated, token]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const period = dashboard?.period || {};
  const obligations = dashboard?.obligations || {};
  const forecast = dashboard?.forecast || {};
  const cash = dashboard?.cash || {};
  const credit = dashboard?.credit || {};
  const income = dashboard?.income || {};
  const riskLevel = forecast.risk_level || 'ok';
  const riskCopy = getRiskCopy(riskLevel, forecast);

  return (
    <div className="home">
      <Navbar toggleMenu={toggleMenu} onLogout={onLogout} />
      <main className="content">
        <div className={`cash-cockpit ${menuOpen ? 'menu-open' : ''}`}>
          <header className={`cockpit-hero risk-${riskLevel}`}>
            <div>
              <span className="eyebrow">Planning window</span>
              <h1>{riskCopy.title}</h1>
              <p>
                {period.label || 'Fri-Thu'}: {formatDate(period.start)} - {formatDate(period.end)}.
                Forecast through {formatDate(period.forecast_end)}.
              </p>
            </div>
            <div className="hero-metrics" aria-label="Planning totals">
              <div>
                <span>Cash now</span>
                <strong>{formatMoney(cash.total)}</strong>
              </div>
              <div>
                <span>30d end</span>
                <strong>{formatMoney(forecast.next_30_days_cash_after_payments)}</strong>
              </div>
              <div>
                <span>Credit max</span>
                <strong>{formatMoney(credit.max_single_card_available)}</strong>
              </div>
            </div>
          </header>

          {loading ? (
            <section className="panel loading-panel">Loading planning data...</section>
          ) : error ? (
            <section className="panel error-text">{error}</section>
          ) : (
            <>
              <section className="panel chart-panel">
                <div className="panel-heading">
                  <div>
                    <span className="eyebrow">Cash flow</span>
                    <h2>Money in, pressure out</h2>
                  </div>
                  <p>{riskCopy.detail}</p>
                </div>
                <CashFlowChart daily={forecast.daily} />
              </section>

              <section className="cockpit-grid">
                <section className="panel">
                  <div className="panel-heading">
                    <div>
                      <span className="eyebrow">Waterfall</span>
                      <h3>Why the forecast ends there</h3>
                    </div>
                  </div>
                  <WaterfallChart cash={cash} income={income} obligations={obligations} forecast={forecast} />
                </section>

                <section className="panel">
                  <div className="panel-heading">
                    <div>
                      <span className="eyebrow">Accounts</span>
                      <h3>Where the pressure lands</h3>
                    </div>
                  </div>
                  <AccountLanes accounts={cash.accounts} />
                </section>
              </section>

              <section className="cockpit-grid lower-grid">
                <AgentCheckIn forecast={forecast} onDashboardRefresh={() => fetchDashboard({ silent: true })} />

                <section className="panel">
                  <div className="panel-heading">
                    <div>
                      <span className="eyebrow">Next moves</span>
                      <h3>Transfers and warnings</h3>
                    </div>
                    <Link to="/payments" className="icon-link">Payments</Link>
                  </div>
                  <div className="notes-list">
                    {(dashboard.transfer_suggestions || []).map((item, index) => (
                      <div className="note transfer-note" key={`transfer-${index}`}>
                        Move {formatMoney(item.amount)} from {item.from_account} to {item.to_account} before {formatDate(item.before_date)}.
                      </div>
                    ))}
                    {(forecast.warnings || []).map((warning, index) => (
                      <div className="note warning-note" key={`warning-${index}`}>{warning}</div>
                    ))}
                    {(forecast.suggestions || []).map((suggestion, index) => (
                      <div className="note suggestion-note" key={`suggestion-${index}`}>{suggestion}</div>
                    ))}
                    {!(dashboard.transfer_suggestions || []).length && !(forecast.warnings || []).length && !(forecast.suggestions || []).length && (
                      <div className="empty-state">No warnings in this planning window.</div>
                    )}
                  </div>
                </section>
              </section>
            </>
          )}
        </div>
      </main>
    </div>
  );
}

export default Home;
